import { API_BASE } from '@/shared/services/api';
/**
 * User Settings Page
 * Profile, Preferences (including Language), Security, and Notifications
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { toast } from 'sonner';
import { cn } from '@/shared/utils';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Separator } from '@/shared/components/ui/separator';
import { Switch } from '@/shared/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  User, Settings, Shield, Bell, Globe, Moon, Sun, Monitor,
  Check, Save, Eye, EyeOff, RefreshCw, Camera
} from 'lucide-react';
import { useRef } from 'react';

const API_URL = API_BASE;
const DATE_FORMAT_KEY = 'ezerp_date_format';
const TIME_FORMAT_KEY = 'ezerp_time_format';

const splitFullName = (fullName) => {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', middleName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], middleName: '', lastName: '' };
  if (parts.length === 2) return { firstName: parts[0], middleName: '', lastName: parts[1] };
  return {
    firstName: parts[0],
    middleName: parts.slice(1, -1).join(' '),
    lastName: parts[parts.length - 1]
  };
};

const getUserNameParts = (user) => {
  const fallback = splitFullName(user?.full_name);
  return {
    firstName: user?.first_name || fallback.firstName,
    middleName: user?.middle_name || fallback.middleName,
    lastName: user?.last_name || fallback.lastName
  };
};

export default function UserSettings() {
  const { t } = useTranslation(['settings', 'common']);
  const { user, token, refreshUser } = useAuth();
  const { currentLanguage, setLanguage, supportedLanguages, isChanging } = useLanguage();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  
  // Profile state
  const initialNameParts = getUserNameParts(user);
  const [profile, setProfile] = useState({
    firstName: initialNameParts.firstName,
    middleName: initialNameParts.middleName,
    lastName: initialNameParts.lastName,
    username: user?.username || '',
    email: user?.email || '',
    phone: user?.phone || '',
    department: user?.department || '',
    photo_url: user?.photo_url || ''
  });
  const [photoLoadFailed, setPhotoLoadFailed] = useState(false);
  
  // Preferences state
  const [preferences, setPreferences] = useState({
    language: currentLanguage,
    theme: localStorage.getItem('theme') || 'system',
    dateFormat: localStorage.getItem(DATE_FORMAT_KEY) || 'DD/MM/YYYY',
    timeFormat: localStorage.getItem(TIME_FORMAT_KEY) || '24',
  });
  
  // Password state
  const [passwordForm, setPasswordForm] = useState({
    current: '',
    new: '',
    confirm: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Notifications state
  const [notifications, setNotifications] = useState({
    email_notifications: user?.email_notifications ?? true,
    push_notifications: user?.push_notifications ?? true,
    sms_notifications: user?.sms_notifications ?? false,
    weekly_digest: user?.weekly_digest ?? true
  });

  useEffect(() => {
    if (user) {
      const nameParts = getUserNameParts(user);
      setProfile(prev => ({
        ...prev,
        firstName: nameParts.firstName || '',
        middleName: nameParts.middleName || '',
        lastName: nameParts.lastName || '',
        username: user.username || '',
        email: user.email || '',
        phone: user.phone || '',
        department: user.department || '',
        photo_url: user.photo_url || ''
      }));
      setPhotoLoadFailed(false);
      setNotifications({
        email_notifications: user.email_notifications ?? true,
        push_notifications: user.push_notifications ?? true,
        sms_notifications: user.sms_notifications ?? false,
        weekly_digest: user.weekly_digest ?? true
      });
    }
  }, [user]);

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/user/upload-photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setProfile({ ...profile, photo_url: data.photo_url });
        setPhotoLoadFailed(false);
        await refreshUser();
        toast.success(t('preferences.savedSuccessfully'));
      } else {
        toast.error(t('common:operationFailed'));
      }
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error(t('common:operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Handle language change
  const handleLanguageChange = async (langCode) => {
    setPreferences(prev => ({ ...prev, language: langCode }));
    await setLanguage(langCode);
    toast.success(t('preferences.savedSuccessfully'));
  };

  // Handle theme change
  const handleThemeChange = (theme) => {
    setPreferences(prev => ({ ...prev, theme }));
    localStorage.setItem('theme', theme);
    
    // Apply theme
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else if (theme === 'light') {
      root.classList.remove('dark');
    } else {
      // System preference
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    }
    
    toast.success(t('preferences.savedSuccessfully'));
  };

  const handleDateFormatChange = (dateFormat) => {
    setPreferences(prev => ({ ...prev, dateFormat }));
    localStorage.setItem(DATE_FORMAT_KEY, dateFormat);
    toast.success(t('preferences.savedSuccessfully'));
  };

  const handleTimeFormatChange = (timeFormat) => {
    setPreferences(prev => ({ ...prev, timeFormat }));
    localStorage.setItem(TIME_FORMAT_KEY, timeFormat);
    toast.success(t('preferences.savedSuccessfully'));
  };

  // Save profile
  const handleSaveProfile = async () => {
    if (!profile.firstName.trim()) {
      toast.error('First name is required');
      return;
    }
    if (!profile.lastName.trim()) {
      toast.error('Last name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        first_name: profile.firstName.trim(),
        middle_name: profile.middleName.trim() || null,
        last_name: profile.lastName.trim(),
        username: profile.username.trim() || null,
        full_name: [profile.firstName, profile.middleName, profile.lastName]
          .map(part => part.trim())
          .filter(Boolean)
          .join(' '),
        phone: profile.phone,
        department: profile.department
      };
      
      const response = await fetch(`${API_URL}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        await refreshUser();
        toast.success(t('profileInfo.profileUpdated'));
      } else {
        toast.error(t('common:operationFailed'));
      }
    } catch (error) {
      toast.error(t('common:operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error(t('common:errors.passwordMismatch', { ns: 'errors' }));
      return;
    }
    if (passwordForm.new.length < 8) {
      toast.error(t('settings:passwordTooShort'));
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/user/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordForm.current,
          new_password: passwordForm.new
        })
      });
      
      if (response.ok) {
        toast.success(t('security.changePassword.success'));
        setPasswordForm({ current: '', new: '', confirm: '' });
      } else {
        toast.error(t('common:operationFailed'));
      }
    } catch (error) {
      toast.error(t('common:operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Save Notifications
  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/user/notifications-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(notifications)
      });
      if (response.ok) {
        await refreshUser();
        toast.success(t('preferences.savedSuccessfully'));
      } else {
        toast.error(t('common:operationFailed'));
      }
    } catch (err) {
      toast.error(t('common:operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="user-settings-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div
          className="relative w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-white text-2xl font-bold cursor-pointer group"
          onClick={handlePhotoClick}
        >
          {profile.photo_url && !photoLoadFailed ? (
            <img
              src={profile.photo_url.startsWith('http') ? profile.photo_url : `${API_URL}${profile.photo_url}`}
              alt={user?.full_name}
              className="w-full h-full object-cover"
              onError={() => setPhotoLoadFailed(true)}
            />
          ) : (
            <User className="h-10 w-10 text-white" />
          )}
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera className="w-6 h-6 text-white" />
          </div>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/*"
          onChange={handleFileChange}
        />
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-7 w-7 text-primary" />
            {t('title')}
          </h1>
          <p className="text-muted-foreground">{t('subtitle')}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={handlePhotoClick}
          >
            <Camera className="mr-2 h-4 w-4" />
            {t('profileInfo.changeAvatar')}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="profile" className="gap-2">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">{t('tabs.profile')}</span>
          </TabsTrigger>
          <TabsTrigger value="preferences" className="gap-2">
            <Globe className="h-4 w-4" />
            <span className="hidden sm:inline">{t('tabs.preferences')}</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">{t('tabs.security')}</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2">
            <Bell className="h-4 w-4" />
            <span className="hidden sm:inline">{t('tabs.notifications')}</span>
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>{t('profileInfo.title')}</CardTitle>
              <CardDescription>{t('profileInfo.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>{t('profileInfo.firstName')}</Label>
                  <Input
                    value={profile.firstName}
                    onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('profileInfo.middleName')}</Label>
                  <Input
                    value={profile.middleName}
                    onChange={(e) => setProfile({ ...profile, middleName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('profileInfo.lastName')}</Label>
                  <Input
                    value={profile.lastName}
                    onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('profileInfo.aliasUser')}</Label>
                <Input
                  value={profile.username}
                  onChange={(e) => setProfile({ ...profile, username: e.target.value })}
                  placeholder="alias.user"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('profileInfo.email')}</Label>
                <Input
                  type="email"
                  value={profile.email}
                  readOnly
                  disabled
                  aria-readonly="true"
                  className="bg-muted/50 text-muted-foreground"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('profileInfo.phone')}</Label>
                  <Input
                    type="tel"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('common:department') || 'Department'}</Label>
                  <Input
                    value={profile.department}
                    onChange={(e) => setProfile({ ...profile, department: e.target.value })}
                  />
                </div>
              </div>
              <Button onClick={handleSaveProfile} disabled={saving}>
                {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                {t('profileInfo.saveChanges')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preferences Tab */}
        <TabsContent value="preferences" className="space-y-6">
          {/* Language Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                {t('preferences.language.title')}
              </CardTitle>
              <CardDescription>{t('preferences.language.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {supportedLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    disabled={isChanging}
                    className={cn(
                      "flex items-center gap-3 p-4 rounded-lg border-2 transition-all text-left",
                      "hover:border-primary/50 hover:bg-muted/50",
                      currentLanguage === lang.code 
                        ? "border-primary bg-primary/5" 
                        : "border-muted"
                    )}
                    data-testid={`lang-card-${lang.code}`}
                  >
                    <span className="text-2xl">{lang.flag}</span>
                    <div className="flex-1">
                      <p className="font-medium">{lang.nativeName}</p>
                      <p className="text-xs text-muted-foreground">{lang.name}</p>
                    </div>
                    {currentLanguage === lang.code && (
                      <Check className="h-5 w-5 text-primary shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Theme Selection */}
          <Card>
            <CardHeader>
              <CardTitle>{t('preferences.theme.title')}</CardTitle>
              <CardDescription>{t('preferences.theme.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => handleThemeChange('light')}
                  className={cn(
                    "flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all",
                    "hover:border-primary/50 hover:bg-muted/50",
                    preferences.theme === 'light' ? "border-primary bg-primary/5" : "border-muted"
                  )}
                >
                  <Sun className="h-6 w-6" />
                  <span className="text-sm font-medium">{t('preferences.theme.light')}</span>
                </button>
                <button
                  onClick={() => handleThemeChange('dark')}
                  className={cn(
                    "flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all",
                    "hover:border-primary/50 hover:bg-muted/50",
                    preferences.theme === 'dark' ? "border-primary bg-primary/5" : "border-muted"
                  )}
                >
                  <Moon className="h-6 w-6" />
                  <span className="text-sm font-medium">{t('preferences.theme.dark')}</span>
                </button>
                <button
                  onClick={() => handleThemeChange('system')}
                  className={cn(
                    "flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all",
                    "hover:border-primary/50 hover:bg-muted/50",
                    preferences.theme === 'system' ? "border-primary bg-primary/5" : "border-muted"
                  )}
                >
                  <Monitor className="h-6 w-6" />
                  <span className="text-sm font-medium">{t('preferences.theme.system')}</span>
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Date & Time Format */}
          <Card>
            <CardHeader>
              <CardTitle>{t('preferences.dateFormat.title')}</CardTitle>
              <CardDescription>{t('preferences.dateFormat.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('preferences.dateFormat.title')}</Label>
                  <Select
                    value={preferences.dateFormat}
                    onValueChange={handleDateFormatChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('preferences.timeFormat.title')}</Label>
                  <Select
                    value={preferences.timeFormat}
                    onValueChange={handleTimeFormatChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12">{t('preferences.timeFormat.12hour')}</SelectItem>
                      <SelectItem value="24">{t('preferences.timeFormat.24hour')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle>{t('security.changePassword.title')}</CardTitle>
              <CardDescription>{t('security.changePassword.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('security.changePassword.current')}</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordForm.current}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  >
                    {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('security.changePassword.new')}</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordForm.new}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  >
                    {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('security.changePassword.confirm')}</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordForm.confirm}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  >
                    {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{t('security.changePassword.requirements')}</p>
              <Button 
                onClick={handleChangePassword} 
                disabled={saving || !passwordForm.current || !passwordForm.new || !passwordForm.confirm}
              >
                {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                {t('security.changePassword.button')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>{t('notifications.title')}</CardTitle>
              <CardDescription>{t('notifications.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t('notifications.email.title')}</p>
                  <p className="text-sm text-muted-foreground">{t('notifications.email.subtitle')}</p>
                </div>
                <Switch 
                  checked={notifications.email_notifications}
                  onCheckedChange={(c) => setNotifications({...notifications, email_notifications: c})}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t('notifications.push.title')}</p>
                  <p className="text-sm text-muted-foreground">{t('notifications.push.subtitle')}</p>
                </div>
                <Switch 
                  checked={notifications.push_notifications}
                  onCheckedChange={(c) => setNotifications({...notifications, push_notifications: c})}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t('notifications.sms.title')}</p>
                  <p className="text-sm text-muted-foreground">{t('notifications.sms.subtitle')}</p>
                </div>
                <Switch 
                  checked={notifications.sms_notifications}
                  onCheckedChange={(c) => setNotifications({...notifications, sms_notifications: c})}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t('settings:weeklyDigest')}</p>
                  <p className="text-sm text-muted-foreground">{t('settings:receiveWeeklyDigest')}</p>
                </div>
                <Switch 
                  checked={notifications.weekly_digest}
                  onCheckedChange={(c) => setNotifications({...notifications, weekly_digest: c})}
                />
              </div>
              <Button onClick={handleSaveNotifications} disabled={saving} className="mt-4">
                {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                {t('common:saveChanges')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
